import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Badge, Box, Button, Flex, Heading, Select, Switch, Table, Text, TextField } from '@radix-ui/themes'
import { useMemo } from 'react'
import { Ops } from '@willie/schemas'
import { useTRPC, type TRPCOutputs } from '~/utils/trpc'
import { useMeterMakerCatalog, withSelectedMakers } from '~/features/installations/useMeterMakers'
import {
  INSTALLATION_PROFILE_OPTIONS,
  LOGGER_MAKER_OPTIONS,
  COMPATIBILITY_STATUS_OPTIONS,
  PENDING_FILTER,
  STAGE_OPTIONS,
  compatibilityStatusColor,
  compatibilityStatusLabel,
  installationRowClass,
  loggerMakerLabel,
  stageColor,
  stageLabel,
  type InstallationProfile,
  type CompatibilityStatus,
  type LoggerMaker,
  type MeterMaker,
  type Stage
} from '~/features/installations/constants'
import { computeStage, isRetired, needsCompatibilityCheck, needsVerification } from '~/features/installations/service'
import { TicketBadge } from '~/features/freshdesk/components/TicketBadge'
import { CompatibilityBadge } from '~/features/installations/components/CompatibilityBadge'

type Installation = TRPCOutputs['ops']['stationInstallations']['list'][number]

interface InstallationsSearch {
  orgId?: string
  stage?: Stage
  compatibilityStatus?: CompatibilityStatus
  meterMaker?: MeterMaker
  loggerMaker?: LoggerMaker
  installationProfile?: InstallationProfile
  q?: string
  installationIds?: number[]
  stalledOnly?: boolean
  verificationNeeded?: boolean
  compatibilityToCheck?: boolean
  showRetired?: boolean
}

const parseEnum = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : undefined

const parseMeterMaker = (value: unknown): MeterMaker | undefined => {
  const parsed = Ops.MeterMakers.meterMakerSlugSchema.safeParse(value)
  return parsed.success ? parsed.data : undefined
}

const parseInstallationProfile = (value: unknown): InstallationProfile | undefined => {
  const parsed = Ops.InstallationProfiles.installationProfileSchema.safeParse(value)
  return parsed.success ? parsed.data : undefined
}

const parseIdList = (value: unknown): number[] | undefined => {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : null
  if (!raw) return undefined
  const ids = raw
    .map((v) => (typeof v === 'number' ? v : parseInt(String(v), 10)))
    .filter((n) => Number.isInteger(n) && n > 0)
  return ids.length > 0 ? ids : undefined
}

const byOldestSubmission = (a: Installation, b: Installation): number =>
  (a.submitted?.occurred_at ?? '').localeCompare(b.submitted?.occurred_at ?? '')

const byNewestStall = (a: Installation, b: Installation): number =>
  (b.current_stall?.occurred_at ?? '').localeCompare(a.current_stall?.occurred_at ?? '')

const PendingBadge = () => (
  <Badge color={compatibilityStatusColor.pending} variant="soft" size="1">
    {compatibilityStatusLabel.pending}
  </Badge>
)

const InstallationsFilters = ({
  value,
  onChange,
  orgs,
  installationProfiles
}: {
  value: InstallationsSearch
  onChange: (next: InstallationsSearch) => void
  orgs: { id: string; name: string }[]
  installationProfiles: readonly string[]
}) => {
  const meterMakers = useMeterMakerCatalog()
  const selectedMakers = value.meterMaker && value.meterMaker !== PENDING_FILTER ? [value.meterMaker] : []

  const anyFilter =
    !!value.orgId ||
    !!value.stage ||
    !!value.compatibilityStatus ||
    !!value.meterMaker ||
    !!value.loggerMaker ||
    !!value.installationProfile ||
    !!value.q ||
    !!value.installationIds?.length

  return (
    <Flex direction="column" gap="3">
      <Flex gap="3" wrap="wrap" align="end">
        <Box flexGrow="1">
          <Text as="label" size="1" color="gray">
            Search
          </Text>
          <TextField.Root
            placeholder="Name, installation ID, internal ID, contact…"
            value={value.q ?? ''}
            onChange={(e) => onChange({ ...value, q: e.target.value || undefined })}
          />
        </Box>

        <Box width="220px">
          <Text as="label" size="1" color="gray">
            Organization
          </Text>
          <Select.Root
            value={value.orgId ?? 'all'}
            onValueChange={(v) => onChange({ ...value, orgId: v === 'all' ? undefined : v })}
          >
            <Select.Trigger className="w-full" />
            <Select.Content>
              <Select.Item value="all">All organizations</Select.Item>
              {orgs.map((o) => (
                <Select.Item key={o.id} value={o.id}>
                  {o.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>
      </Flex>

      <Flex gap="3" wrap="wrap" align="end">
        <Box flexGrow="1" minWidth="160px">
          <Text as="label" size="1" color="gray">
            Stage
          </Text>
          <Select.Root
            value={value.stage ?? 'all'}
            onValueChange={(v) => onChange({ ...value, stage: v === 'all' ? undefined : (v as Stage) })}
          >
            <Select.Trigger className="w-full" />
            <Select.Content>
              <Select.Item value="all">All stages</Select.Item>
              {STAGE_OPTIONS.map((s) => (
                <Select.Item key={s} value={s}>
                  {stageLabel[s]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>

        <Box flexGrow="1" minWidth="160px">
          <Text as="label" size="1" color="gray">
            Meter compatibility
          </Text>
          <Select.Root
            value={value.compatibilityStatus ?? 'all'}
            onValueChange={(v) =>
              onChange({
                ...value,
                compatibilityStatus: v === 'all' ? undefined : (v as CompatibilityStatus)
              })
            }
          >
            <Select.Trigger className="w-full" />
            <Select.Content>
              <Select.Item value="all">All compatibility</Select.Item>
              {COMPATIBILITY_STATUS_OPTIONS.map((s) => (
                <Select.Item key={s} value={s}>
                  {compatibilityStatusLabel[s]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>

        <Box flexGrow="1" minWidth="160px">
          <Text as="label" size="1" color="gray">
            Meter maker
          </Text>
          <Select.Root
            value={value.meterMaker ?? 'all'}
            onValueChange={(v) =>
              onChange({ ...value, meterMaker: v === 'all' ? undefined : (v as MeterMaker) })
            }
          >
            <Select.Trigger className="w-full" />
            <Select.Content>
              <Select.Item value="all">All makers</Select.Item>
              <Select.Item value={PENDING_FILTER}>{compatibilityStatusLabel.pending}</Select.Item>
              {withSelectedMakers(meterMakers, selectedMakers).map((maker) => (
                <Select.Item key={maker.slug} value={maker.slug}>
                  {maker.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>

        <Box flexGrow="1" minWidth="160px">
          <Text as="label" size="1" color="gray">
            Logger provider
          </Text>
          <Select.Root
            value={value.loggerMaker ?? 'all'}
            onValueChange={(v) =>
              onChange({ ...value, loggerMaker: v === 'all' ? undefined : (v as LoggerMaker) })
            }
          >
            <Select.Trigger className="w-full" />
            <Select.Content>
              <Select.Item value="all">All providers</Select.Item>
              {LOGGER_MAKER_OPTIONS.map((maker) => (
                <Select.Item key={maker} value={maker}>
                  {loggerMakerLabel[maker]}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>

        <Box flexGrow="1" minWidth="160px">
          <Text as="label" size="1" color="gray">
            Installation profile
          </Text>
          <Select.Root
            value={value.installationProfile ?? 'all'}
            onValueChange={(v) =>
              onChange({
                ...value,
                installationProfile: v === 'all' ? undefined : (v as InstallationProfile)
              })
            }
          >
            <Select.Trigger className="w-full" />
            <Select.Content>
              <Select.Item value="all">All profiles</Select.Item>
              <Select.Item value={PENDING_FILTER}>{compatibilityStatusLabel.pending}</Select.Item>
              {installationProfiles.map((p) => (
                <Select.Item key={p} value={p}>
                  {p}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Box>

        {anyFilter && (
          <Button variant="ghost" color="gray" onClick={() => onChange({})}>
            Clear
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

const InstallationsTable = ({
  installations,
  openTicketCounts,
  onRowClick
}: {
  installations: Installation[]
  openTicketCounts: Record<string, number>
  onRowClick: (id: number) => void
}) => {
  const { labelOf: meterMakerLabel } = useMeterMakerCatalog()

  if (installations.length === 0) {
    return (
      <Box py="6">
        <Text color="gray">No installations match these filters.</Text>
      </Box>
    )
  }

  return (
    <Table.Root variant="surface">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Site</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Meter compatibility</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Meter maker</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Installation profile</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Progress</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {installations.map((row) => (
          <Table.Row
            key={row.installation_id}
            onClick={() => onRowClick(row.installation_id)}
            className={installationRowClass({
              isStalled: row.current_stall !== null,
              isRetired: isRetired(row)
            })}
          >
            <Table.Cell>
              <Text size="2" className="font-mono">
                #{row.installation_id}
              </Text>
            </Table.Cell>
            <Table.Cell>{row.name ?? '—'}</Table.Cell>
            <Table.Cell>
              {row.site?.name ? (
                <Text size="2">{row.site.name}</Text>
              ) : (
                <Text size="1" color="gray">
                  —
                </Text>
              )}
            </Table.Cell>
            <Table.Cell>
              <CompatibilityBadge status={row.compatibility_status} />
            </Table.Cell>
            <Table.Cell>
              {row.meter_maker ? (
                <Text size="2">{meterMakerLabel(row.meter_maker)}</Text>
              ) : (
                <PendingBadge />
              )}
            </Table.Cell>
            <Table.Cell>
              {row.installation_profile ? (
                <Text size="2">{row.installation_profile}</Text>
              ) : (
                <PendingBadge />
              )}
            </Table.Cell>
            <Table.Cell>
              {(() => {
                const stage = computeStage(row)
                return (
                  <Flex align="center" gap="2">
                    <Badge color={stageColor[stage]} variant="soft" size="1">
                      {stageLabel[stage]}
                    </Badge>
                    <TicketBadge count={openTicketCounts[String(row.installation_id)] ?? 0} />
                    {isRetired(row) && (
                      <Badge color="gray" variant="soft" size="1" title={row.retirement_reason ?? undefined}>
                        retired
                      </Badge>
                    )}
                  </Flex>
                )
              })()}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

const InstallationsListPage = () => {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const search = Route.useSearch()

  const installationsQuery = useQuery(
    trpc.ops.stationInstallations.list.queryOptions({
      orgId: search.orgId,
      stage: search.stage,
      compatibilityStatus: search.compatibilityStatus,
      meterMaker: search.meterMaker,
      loggerMaker: search.loggerMaker,
      installationProfile: search.installationProfile,
      search: search.q,
      installationIds: search.installationIds,
      // Fetched rather than filtered away server-side: a query that hides them cannot say how
      // many it hid, and the count is what the toggle below is offering.
      includeRetired: true
    })
  )

  const orgsQuery = useQuery(trpc.ops.orgs.list.queryOptions())
  const profilesQuery = useQuery(
    trpc.ops.installationProfiles.list.queryOptions(undefined, { staleTime: Infinity })
  )

  const ticketCountsQuery = useQuery(
    trpc.ops.freshdesk.openCounts.queryOptions(undefined, { staleTime: 1000 * 60 * 5 })
  )

  const installations = useMemo(() => installationsQuery.data ?? [], [installationsQuery.data])

  const retiredCount = installations.filter(isRetired).length
  // The other two count what the table can show, so flipping a badge's switch never empties it.
  const listedInstallations = search.showRetired ? installations : installations.filter((i) => !isRetired(i))
  const stalledCount = listedInstallations.filter((i) => i.current_stall !== null).length
  const toVerifyCount = listedInstallations.filter(needsVerification).length
  const toCheckCount = listedInstallations.filter(needsCompatibilityCheck).length

  const filteredInstallations = listedInstallations
    .filter((i) => !search.stalledOnly || i.current_stall !== null)
    .filter((i) => !search.verificationNeeded || needsVerification(i))
    .filter((i) => !search.compatibilityToCheck || needsCompatibilityCheck(i))
  const rowOrder = search.verificationNeeded ? byOldestSubmission : search.stalledOnly ? byNewestStall : null
  const visibleInstallations = rowOrder ? filteredInstallations.sort(rowOrder) : filteredInstallations

  const setSearch = (next: InstallationsSearch) => {
    navigate({ to: '/installations', search: next, replace: true })
  }

  return (
    <Flex direction="column" gap="4">
      <Heading size="6">Installations</Heading>

      <InstallationsFilters
        value={search}
        onChange={setSearch}
        orgs={orgsQuery.data ?? []}
        installationProfiles={profilesQuery.data?.map((profile) => profile.slug) ?? INSTALLATION_PROFILE_OPTIONS}
      />

      {installationsQuery.isLoading ? (
        <Text color="gray">Loading…</Text>
      ) : installationsQuery.error ? (
        <Text color="red">{installationsQuery.error.message}</Text>
      ) : (
        <>
          <Flex align="center" gap="3" wrap="wrap">
            <Text size="1" color="gray">
              {visibleInstallations.length} installation{visibleInstallations.length === 1 ? '' : 's'}
            </Text>
            {stalledCount > 0 && (
              <>
                <Badge color="red" variant="soft" size="1">
                  ⚠ {stalledCount} stalled
                </Badge>
                <Text as="label" size="1" color="gray">
                  <Flex align="center" gap="2">
                    <Switch
                      size="1"
                      color="red"
                      checked={search.stalledOnly ?? false}
                      onCheckedChange={(checked) =>
                        setSearch({ ...search, stalledOnly: checked || undefined })
                      }
                    />
                    Stalled only
                  </Flex>
                </Text>
              </>
            )}
            {toVerifyCount > 0 && (
              <>
                <Badge color="amber" variant="soft" size="1">
                  {toVerifyCount} to verify
                </Badge>
                <Text as="label" size="1" color="gray">
                  <Flex align="center" gap="2">
                    <Switch
                      size="1"
                      color="amber"
                      checked={search.verificationNeeded ?? false}
                      onCheckedChange={(checked) =>
                        setSearch({ ...search, verificationNeeded: checked || undefined })
                      }
                    />
                    To verify only
                  </Flex>
                </Text>
              </>
            )}
            {toCheckCount > 0 && (
              <>
                <Badge color="violet" variant="soft" size="1">
                  {toCheckCount} to check
                </Badge>
                <Text as="label" size="1" color="gray">
                  <Flex align="center" gap="2">
                    <Switch
                      size="1"
                      color="violet"
                      checked={search.compatibilityToCheck ?? false}
                      onCheckedChange={(checked) =>
                        setSearch({ ...search, compatibilityToCheck: checked || undefined })
                      }
                    />
                    To check only
                  </Flex>
                </Text>
              </>
            )}
            {retiredCount > 0 && (
              <>
                <Badge color="gray" variant="soft" size="1">
                  {retiredCount} retired
                </Badge>
                <Text as="label" size="1" color="gray">
                  <Flex align="center" gap="2">
                    <Switch
                      size="1"
                      color="gray"
                      checked={search.showRetired ?? false}
                      onCheckedChange={(checked) => setSearch({ ...search, showRetired: checked || undefined })}
                    />
                    Show retired
                  </Flex>
                </Text>
              </>
            )}
          </Flex>
          <InstallationsTable
            installations={visibleInstallations}
            openTicketCounts={ticketCountsQuery.data?.byInstallationId ?? {}}
            onRowClick={(id) => navigate({ to: '/installations/$installationId', params: { installationId: String(id) } })}
          />
        </>
      )}
    </Flex>
  )
}

export const Route = createFileRoute('/installations/')({
  component: InstallationsListPage,
  validateSearch: (raw: Record<string, unknown>): InstallationsSearch => ({
    orgId: typeof raw.orgId === 'string' ? raw.orgId : undefined,
    stage: parseEnum(raw.stage, STAGE_OPTIONS),
    compatibilityStatus: parseEnum(raw.compatibilityStatus, COMPATIBILITY_STATUS_OPTIONS),
    meterMaker: parseMeterMaker(raw.meterMaker),
    loggerMaker: parseEnum(raw.loggerMaker, LOGGER_MAKER_OPTIONS),
    installationProfile: parseInstallationProfile(raw.installationProfile),
    q: typeof raw.q === 'string' && raw.q.length > 0 ? raw.q : undefined,
    installationIds: parseIdList(raw.installationIds),
    stalledOnly: raw.stalledOnly === true || raw.stalledOnly === 'true' ? true : undefined,
    verificationNeeded: raw.verificationNeeded === true || raw.verificationNeeded === 'true' ? true : undefined,
    compatibilityToCheck:
      raw.compatibilityToCheck === true || raw.compatibilityToCheck === 'true' ? true : undefined,
    showRetired: raw.showRetired === true || raw.showRetired === 'true' ? true : undefined
  })
})
